import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Subject, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { BlogService } from '../services/blog.service';
import { TeamService } from '../services/team.service';
import { SeoService } from '../services/seo.service';
import { BreadcrumbsService } from '../services/breadcrumbs.service';
import { BlogListItem } from '../blog/blog.models';
import { TeamMember } from '../models/team-member.model';

/**
 * /auteur/<slug> — an author's article archive (BLA-5). The slug is the
 * team-member slug, so the profile is reused from the team service and the page
 * links back to the full bio at /over-blauwtand/team/<slug>. To avoid being a
 * near-duplicate of the team page, this page leads with the LIST of articles and
 * only a short byline, and emits a CollectionPage (not a second ProfilePage).
 */
@Component({
  selector: 'app-author-page',
  standalone: false,
  templateUrl: './author-page.component.html',
  styleUrls: ['./author-page.component.scss'],
})
export class AuthorPageComponent implements OnInit, OnDestroy {
  author = signal<TeamMember | null>(null);
  posts = signal<BlogListItem[]>([]);
  isLoading = signal(true);
  notFound = signal(false);

  private readonly destroy$ = new Subject<void>();
  private readonly jsonLdId = 'author-collection';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly blogService: BlogService,
    private readonly teamService: TeamService,
    private readonly seo: SeoService,
    private readonly breadcrumbs: BreadcrumbsService,
  ) {}

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map((params: ParamMap) => params.get('slug') || ''),
        distinctUntilChanged(),
        tap(() => this.resetState()),
        switchMap((slug) =>
          this.teamService.detail(slug).pipe(
            switchMap((profile) =>
              // The posts call can fail independently of the profile; degrade to
              // an empty list rather than failing the whole page.
              this.blogService.list({ author: slug, limit: 24 }).pipe(
                catchError(() => of({ items: [], nextCursor: null })),
                map((res) => ({ slug, profile, posts: res.items ?? [] })),
              ),
            ),
            catchError(() => {
              this.handleNotFound();
              return of(null);
            }),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        if (!result || !result.profile) {
          return;
        }
        this.author.set(result.profile);
        this.posts.set(result.posts);
        this.isLoading.set(false);
        this.notFound.set(false);
        this.applySeo(result.slug, result.profile, result.posts);
        this.breadcrumbs.setCurrentLeafLabel(result.profile.name);
      });
  }

  ngOnDestroy(): void {
    this.seo.removeJsonLd(this.jsonLdId);
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** /over-blauwtand/team/<slug> — the full bio (the "Bio" link target). */
  get teamPagePath(): string | null {
    const a = this.author();
    return a ? `/over-blauwtand/team/${a.slug}` : null;
  }

  /** Author photo URL (the model types photo as required, but the API can omit it). */
  get authorPhotoUrl(): string | null {
    return this.author()?.photo?.url ?? null;
  }

  /** Filler slots so a row with 1–2 articles still shows 3 cards (no lonely card
   *  next to empty columns). Empty when there are 0 or 3+ real articles. */
  get placeholderSlots(): number[] {
    const count = this.posts().length;
    if (count < 1 || count >= 3) return [];
    return Array.from({ length: 3 - count }, (_, i) => i);
  }

  private resetState(): void {
    this.author.set(null);
    this.posts.set([]);
    this.isLoading.set(true);
    this.notFound.set(false);
    this.seo.removeJsonLd(this.jsonLdId);
  }

  private handleNotFound(): void {
    this.isLoading.set(false);
    this.notFound.set(true);
    this.seo.updateSeo({
      title: 'Auteur niet gevonden',
      description: 'Deze auteurspagina bestaat niet of is verplaatst.',
      robots: 'noindex,follow',
    });
  }

  /** Emit SEO + JSON-LD ONCE, after both profile and posts resolved, so an
   *  empty author page never flashes index,follow before going noindex. */
  private applySeo(
    slug: string,
    profile: TeamMember,
    posts: BlogListItem[],
  ): void {
    const canonical = this.seo.generateCanonical(`/auteur/${slug}`);
    const title = `Artikelen van ${profile.name}`;
    const description =
      profile.shortBio ||
      `Lees alle artikelen en stories van ${profile.name} bij Blauwtand.`;
    const image = profile.photo?.url || undefined;

    this.seo.updateSeo({
      title,
      description,
      canonicalUrl: canonical,
      imageUrl: image,
      ogType: 'profile',
      // An author with no published articles carries no unique value — keep it
      // out of the index (it would also near-duplicate the team page).
      robots: posts.length ? 'index,follow' : 'noindex,follow',
    });

    const person: Record<string, unknown> = {
      '@type': 'Person',
      name: profile.name,
      // Point at the canonical profile (the team page), NOT a second ProfilePage.
      url: this.seo.generateCanonical(`/over-blauwtand/team/${slug}`),
      ...(profile.role ? { jobTitle: profile.role } : {}),
      ...(image ? { image } : {}),
    };

    this.seo.addJsonLd(this.jsonLdId, {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: title,
      url: canonical,
      about: person,
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: posts.map((post, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: this.seo.generateCanonical(`/stories/${post.slug}`),
          name: post.title,
        })),
      },
    });
  }
}
